import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box, Typography, Button, IconButton, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Tooltip, CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RepeatIcon from "@material-ui/icons/Repeat";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import moment from "moment-timezone";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import FileManagerSelectorModal from "../../components/FileManagerSelectorModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import AiVideoModal from "../AiVideo/AiVideoModal";
import AiContentFactory from "../../components/AiContentFactory";
import AiPromptViewer from "../../components/AiPromptViewer";
import ConfirmationModal from "../../components/ConfirmationModal";
import Title from "../../components/Title";
import Paper from "@material-ui/core/Paper";
import PageHelpGuide from "../../components/PageHelpGuide";
import { STATUS_SCHEDULER_HELP_DATA } from "../../constants/pageHelp/statusSchedulerHelp";
import StandardTabs from "../../components/StandardTabs";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    ...getStandardButtonStyles(theme).applyButton,
  },
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  root: {
    padding: theme.spacing(3),
    maxWidth: 1400,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.4rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  exclusiveBadge: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}80`,
    borderRadius: 20,
    padding: "2px 12px",
    fontSize: "0.72rem",
    fontWeight: 700,
    marginLeft: theme.spacing(1),
  },
  dayColumn: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    minHeight: 200,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  dayHeader: {
    fontWeight: 700,
    textAlign: "center",
    padding: theme.spacing(1),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  scheduleCard: {
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)",
    boxShadow: theme.palette.type === "dark" ? "0 0 8px rgba(255,255,255,0.1)" : "0 2px 8px rgba(0,0,0,0.06)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05)" : "#FFFFFF",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": { 
      boxShadow: `0 0 15px ${theme.palette.primary.main}99`,
      borderColor: theme.palette.primary.main,
    },
  },
  inactiveCard: {
    opacity: 0.5,
    borderStyle: "dashed",
  },
  repeatBadge: {
    backgroundColor: "#1976d2",
    color: "#fff",
    borderRadius: 20,
    padding: "1px 8px",
    fontSize: "0.65rem",
    marginLeft: 4,
  },
  colorSwatch: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "inline-block",
    border: "1px solid #ccc",
    verticalAlign: "middle",
    marginRight: 4,
  },
  fileInputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  infoBox: {
    backgroundColor: "#e3f2fd",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    fontSize: "0.82rem",
    color: "#1565c0",
  },
}));

const DAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DAY_COLORS = ["#f44336","#ff9800","#ffeb3b","#4caf50","#2196f3","#9c27b0","#00bcd4"];

const emptyForm = {
  whatsappId: "",
  dayOfWeek: 1,
  scheduledTime: "09:00",
  mediaType: "text",
  caption: "",
  mediaUrl: "",
  backgroundColor: "#075e54",
  isActive: true,
  repeatDaily: false,
  renewalTime: "09:00",
  aiGeneration: false,
  aiPrompt: "",
};

export default function WhatsAppStatusScheduler() {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const isDarkMode = theme.palette.type === "dark";
  const isAdmin = Boolean(user?.super || (user?.profile === "admin" && user?.companyId === 1));

  const { get: getCompanySetting } = useCompanySettings();
  const [companyTimezone, setCompanyTimezone] = useState("America/Lima");

  const [connections, setConnections] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("all");
  const [previewMedia, setPreviewMedia] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [aiVideoOpen, setAiVideoOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneFromDay, setCloneFromDay] = useState(null);
  const [cloneToDay, setCloneToDay] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingDay, setDeletingDay] = useState(null);
  const [selectorModalOpen, setSelectorModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [aiFactoryOpen, setAiFactoryOpen] = useState(false);
  const [aiEditSchedule, setAiEditSchedule] = useState(null);
  const [viewSchedule, setViewSchedule] = useState(null);

  const SCHEDULER_TAB_DEFINITIONS = [
    {
      key: "manual",
      labelKey: "statusScheduler.tabs.manual",
      defaultLabel: "Publicaciones Manuales",
      color: "#00E676",
      Icon: EditIcon,
    },
    {
      key: "aiImage",
      labelKey: "statusScheduler.tabs.aiImage",
      defaultLabel: "Publicaciones con IA (Imagen)",
      color: "#00E5FF",
      Icon: ImageIcon,
    },
    {
      key: "aiVideo",
      labelKey: "statusScheduler.tabs.aiVideo",
      defaultLabel: "Publicaciones con IA (Video)",
      color: "#E040FB",
      Icon: VideocamIcon,
      isBeta: true,
    },
  ];

  const handleTabChange = (e, newValue) => {
    if (newValue === 2 && !isAdmin) {
      toastError(
        i18n.t("statusScheduler.tabs.aiVideoBetaRestricted") ||
        "Esta funcionalidad con IA de Video se encuentra en fase Beta exclusiva para el administrador."
      );
      return;
    }
    setTabValue(newValue);
  };

  const renderSchedulerTabLabel = (index) => {
    const tabDef = SCHEDULER_TAB_DEFINITIONS[index];
    const isActive = tabValue === index;
    const isVideoRestricted = tabDef.isBeta && !isAdmin;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          color: isActive
            ? (isDarkMode ? "#ffffff" : "#111827")
            : (isDarkMode ? "rgba(255, 255, 255, 0.65)" : "rgba(0, 0, 0, 0.6)"),
          transition: "all 0.25s ease",
          userSelect: "none",
          opacity: isVideoRestricted ? 0.75 : 1,
        }}
      >
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px" }}>
          <tabDef.Icon
            style={{
              marginRight: 8,
              color: tabDef.color,
              filter: isActive ? `drop-shadow(0 0 3px ${tabDef.color})` : "none",
              opacity: isActive ? 1 : 0.65,
              fontSize: "1.35rem",
              transition: "all 0.25s ease",
            }}
          />
          <span style={{
            fontWeight: isActive ? 900 : 600,
            lineHeight: 1, // Previene expansión vertical
            textShadow: isActive ? (isDarkMode ? "0 0 3px rgba(255, 255, 255, 0.4)" : "none") : "none",
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
          }}>
            {i18n.t(tabDef.labelKey) || tabDef.defaultLabel}
          </span>
          {tabDef.isBeta && (
            <span
              style={{
                marginLeft: 8,
                padding: "2px 7px",
                borderRadius: 10,
                fontSize: "0.65rem",
                fontWeight: 800,
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #FF6D00 0%, #FF1744 100%)",
                color: "#ffffff",
                boxShadow: "0 0 5px rgba(255, 109, 0, 0.4)",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Beta
            </span>
          )}
        </div>
        <div style={{
          height: 3,
          minHeight: 3,
          flexShrink: 0,
          width: "100%",
          backgroundColor: isActive ? tabDef.color : "transparent",
          boxShadow: isActive ? `0 0 6px ${tabDef.color}` : "none",
          borderRadius: "3px 3px 0 0",
          transition: "all 0.25s ease",
          marginBottom: isActive ? "2px" : "0px", // Evita recorte en el desbordamiento
        }} />
      </div>
    );
  };

  const fileInputRef = React.useRef(null);
  const dayFileInputRef = React.useRef(null);
  const [targetImportDay, setTargetImportDay] = useState(null);

  const handleExportWeek = () => {
    if (schedules.length === 0) return;
    const data = JSON.stringify(schedules, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `states_week_${selectedWhatsapp === "all" ? "all" : selectedWhatsapp}_${moment().format('YYYY-MM-DD_HH-mm')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportDay = (dayIdx) => {
    const daySchedules = schedulesByDay(dayIdx);
    if (daySchedules.length === 0) return;
    const data = JSON.stringify(daySchedules, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `states_day_${dayIdx}_${selectedWhatsapp === "all" ? "all" : selectedWhatsapp}_${moment().format('YYYY-MM-DD_HH-mm')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportWeek = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      showSnack(i18n.t("statusScheduler.selectConnectionTitleWp"), "error");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!Array.isArray(imported)) throw new Error("Invalid format");
        
        setLoading(true);
        for (const s of imported) {
          const payload = {
            whatsappId: selectedWhatsapp,
            dayOfWeek: s.dayOfWeek,
            scheduledTime: s.scheduledTime,
            mediaType: s.mediaType,
            caption: s.caption,
            mediaUrl: s.mediaUrl,
            backgroundColor: s.backgroundColor,
            isActive: s.isActive,
            repeatDaily: s.repeatDaily,
            renewalTime: s.renewalTime,
            aiGeneration: s.aiGeneration,
            aiPrompt: s.aiPrompt
          };
          await api.post("/whatsapp-status-schedule", payload);
        }
        showSnack(`✅ ${imported.length} estado(s) importados correctamente.`);
        loadSchedules();
      } catch (err) {
        showSnack(i18n.t("statusScheduler.errorLoad"), "error");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };


  const handleImportDay = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (targetImportDay === null) return;
    
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      showSnack(i18n.t("statusScheduler.selectConnectionTitleWp"), "error");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!Array.isArray(imported)) throw new Error("Invalid format");
        
        setLoading(true);
        for (const s of imported) {
          const payload = {
            whatsappId: selectedWhatsapp,
            dayOfWeek: targetImportDay,
            scheduledTime: s.scheduledTime,
            mediaType: s.mediaType,
            caption: s.caption,
            mediaUrl: s.mediaUrl,
            backgroundColor: s.backgroundColor,
            isActive: s.isActive,
            repeatDaily: s.repeatDaily,
            renewalTime: s.renewalTime,
            aiGeneration: s.aiGeneration,
            aiPrompt: s.aiPrompt
          };
          await api.post("/whatsapp-status-schedule", payload);
        }
        showSnack(`✅ ${imported.length} estado(s) importados correctamente.`);
        loadSchedules();
      } catch (err) {
        showSnack(i18n.t("statusScheduler.errorLoad"), "error");
      } finally {
        setLoading(false);
        setTargetImportDay(null);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };


  useEffect(() => {
    getCompanySetting({ column: "timezone" }).then(res => {
      if (res && res.timezone) {
        setCompanyTimezone(res.timezone);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/whatsapp?session=0").then(({ data }) => {
      const baileys = (data || []).filter(
        w => !w.channel || w.channel === "whatsapp"
      );
      setConnections(baileys);
      if (baileys.length > 0) setSelectedWhatsapp("all");
    });
  }, []);

  const loadSchedules = useCallback(async () => {
    if (!selectedWhatsapp) return;
    setLoading(true);
    try {
      const { data } = await api.get(selectedWhatsapp === "all" ? "/whatsapp-status-schedule" : `/whatsapp-status-schedule?whatsappId=${selectedWhatsapp}`);
      setSchedules(data);
    } catch {
      showSnack(i18n.t("statusScheduler.errorLoad"), "error");
    } finally {
      setLoading(false);
    }
  }, [selectedWhatsapp]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const getErrorMessage = (err, defaultMsgKey = "statusScheduler.errorSave") => {
    const errorMsg = err?.response?.data?.error;
    if (errorMsg && i18n.exists(`backendErrors.${errorMsg}`)) {
      return i18n.t(`backendErrors.${errorMsg}`);
    }
    return errorMsg || err?.message || i18n.t(defaultMsgKey) || "Error al guardar";
  };

  const handleVideoGenerated = async (videoUrl, prompt) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") return;
    try {
      const payload = {
        scheduledAt: moment().tz(companyTimezone).toISOString(),
        mediaType: "video",
        mediaUrl: videoUrl,
        caption: "",
        aiGeneration: true,
        aiPrompt: prompt || "Video Generado",
        isActive: false, // Guardado como borrador
        repeatDaily: false,
        whatsappId: selectedWhatsapp
      };
      await api.post("/whatsapp-status-schedule", payload);
      loadSchedules();
    } catch (err) {
      console.error("Error creating status video draft", err);
      showSnack(getErrorMessage(err), "error");
    }
  };

  const openAdd = () => {
    setForm({ ...emptyForm, whatsappId: selectedWhatsapp });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setForm({
      whatsappId: s.whatsappId,
      dayOfWeek: s.dayOfWeek,
      scheduledTime: s.scheduledTime,
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      backgroundColor: s.backgroundColor || "#075e54",
      isActive: s.isActive,
      repeatDaily: s.repeatDaily,
      renewalTime: s.renewalTime || "09:00",
      aiGeneration: s.aiGeneration || false,
      aiPrompt: s.aiPrompt || "",
    });
    setEditingId(s.id);
    setModalOpen(true);
  };

  const openCloneSingle = (s) => {
    setForm({
      whatsappId: s.whatsappId,
      dayOfWeek: s.dayOfWeek,
      scheduledTime: s.scheduledTime,
      mediaType: s.mediaType,
      caption: s.caption || "",
      mediaUrl: s.mediaUrl || "",
      backgroundColor: s.backgroundColor || "#075e54",
      isActive: s.isActive,
      repeatDaily: s.repeatDaily,
      renewalTime: s.renewalTime || "09:00",
      aiGeneration: s.aiGeneration || false,
      aiPrompt: s.aiPrompt || "",
    });
    setEditingId(null); 
    setModalOpen(true);
  };

  const handleSave = async () => {
    const now = moment().tz(companyTimezone);
    const currentDay = now.day();
    if (Number(form.dayOfWeek) === currentDay) {
      const [schedHour, schedMin] = form.scheduledTime.split(":").map(Number);
      const scheduledDate = moment().tz(companyTimezone).hours(schedHour).minutes(schedMin).seconds(0).milliseconds(0);
      
      const diffMinutes = scheduledDate.diff(now, 'minutes');
      
      if (diffMinutes < 5) {
        showSnack(i18n.t("statusScheduler.errorTimeMargin"), "error");
        return;
      }
    }

    try {
      const payload = { ...form };
      if (editingId) {
        await api.put(`/whatsapp-status-schedule/${editingId}`, payload);
      } else {
        await api.post("/whatsapp-status-schedule", payload);
      }
      setModalOpen(false);
      showSnack(i18n.t("statusScheduler.saved"));
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err), "error");
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setConfirmModalOpen(true);
  };

  const handleToggleActive = async (s) => {
    try {
      await api.put(`/whatsapp-status-schedule/${s.id}`, { isActive: !s.isActive });
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err), "error");
    }
  };

  const handleDeleteDay = (dayIdx) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      showSnack(i18n.t("statusScheduler.errorSelectConnection"), "error");
      return;
    }
    setDeletingDay(dayIdx);
    setConfirmModalOpen(true);
  };

  const executeDelete = async () => {
    if (deletingDay !== null) {
      const translatedDay = i18n.t(`statusScheduler.days.${deletingDay}`, { defaultValue: DAYS[deletingDay] });
      try {
        await api.delete(`/whatsapp-status-schedule/day/${selectedWhatsapp}/${deletingDay}`);
        showSnack("✅ " + i18n.t("statusScheduler.dayDeleted", { day: translatedDay }));
        loadSchedules();
      } catch (err) {
        showSnack(getErrorMessage(err, "statusScheduler.errorDelete"), "error");
      }
      setDeletingDay(null);
    } else if (deletingId !== null) {
      try {
        await api.delete(`/whatsapp-status-schedule/${deletingId}`);
        showSnack(i18n.t("statusScheduler.deleted"));
        loadSchedules();
      } catch (err) {
        showSnack(getErrorMessage(err, "statusScheduler.errorDelete"), "error");
      }
      setDeletingId(null);
    }
    setConfirmModalOpen(false);
  };

  const schedulesByDay = (day) => filteredSchedules.filter(s => s.dayOfWeek === day);

  const filteredSchedules = schedules.filter(s => {
    const isVideo = s.mediaType === "video";
    if (tabValue === 0) return !s.aiGeneration;
    if (tabValue === 1) return s.aiGeneration && !isVideo;
    if (tabValue === 2) return s.aiGeneration && isVideo;
    return true;
  });

  const openCloneDialog = (dayIdx) => {
    if (!selectedWhatsapp || selectedWhatsapp === "all") {
      showSnack(i18n.t("statusScheduler.errorSelectConnection"), "error");
      return;
    }
    setCloneFromDay(dayIdx);
    setCloneToDay(dayIdx === 0 ? 1 : 0);
    setCloneDialogOpen(true);
  };

  const handleClone = async () => {
    setCloning(true);
    try {
      const { data } = await api.post("/whatsapp-status-schedule/clone", {
        whatsappId: selectedWhatsapp,
        fromDay: cloneFromDay,
        toDay: cloneToDay,
      });
      setCloneDialogOpen(false);
      showSnack(`✅ ${data.cloned} estado(s) clonados al ${DAYS[cloneToDay]} correctamente.`);
      loadSchedules();
    } catch (err) {
      showSnack(getErrorMessage(err), "error");
    } finally {
      setCloning(false);
    }
  };

  const MediaIcon = ({ type }) => {
    if (type === "image") return <ImageIcon fontSize="small" style={{ color: "#43a047" }} />;
    if (type === "video") return <VideocamIcon fontSize="small" style={{ color: "#e53935" }} />;
    return <TextFieldsIcon fontSize="small" style={{ color: "#1976d2" }} />;
  };

  const getBase = () => process.env.REACT_APP_BACKEND_URL || '';
  const getMediaUrl = (relPath, companyId) => {
    const base = getBase();
    if (!relPath) return '';
    if (relPath.startsWith('http')) return relPath;
    
    if (relPath.includes('public/')) {
        return `${base}/public/${relPath.split('public/')[1]}`;
    }
    
    if (relPath.startsWith('ai_img_')) return `${base}/public/company${companyId || 1}/campaigns/${relPath.split('/').pop()}`;
    if (relPath.startsWith('/campaigns/')) return `${base}/public/company${companyId || 1}${relPath}`;
    
    return `${base}/public/company${companyId || 1}/files/${relPath.split('/').pop()}`;
  };

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <PageHelpGuide
              helpData={STATUS_SCHEDULER_HELP_DATA}
              tooltipText="Guía y Ayuda de Estados (Haz clic para ver)"
            />
            <Title>
              {i18n.t("statusScheduler.title")}
              <span className={classes.exclusiveBadge}>{i18n.t("statusScheduler.onlyBaileys")}</span>
            </Title>
          </div>
          <MainHeaderButtonsWrapper>
          </MainHeaderButtonsWrapper>
        </MainHeader>

      <Box mb={3}>
        <Alert 
          severity="success" 
          variant="outlined" 
          style={{ 
            backgroundColor: "rgba(0, 255, 128, 0.05)", 
            border: "1px solid #00ff80", 
            boxShadow: "0 0 12px rgba(0, 255, 128, 0.3)",
            borderRadius: "12px",
            color: "#00ff80",
            marginBottom: "8px",
            fontWeight: 500
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.alertNewFeature") }} />
        </Alert>
        <Alert 
          severity="info" 
          variant="outlined" 
          style={{ 
            backgroundColor: "rgba(0, 191, 255, 0.05)", 
            border: "1px solid #00bfff", 
            boxShadow: "0 0 12px rgba(0, 191, 255, 0.3)",
            borderRadius: "12px",
            color: "#00bfff",
            fontWeight: 500,
            marginBottom: "8px"
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.alertConfigRequired") }} />
        </Alert>
        <Alert severity="success" variant="outlined" style={{ borderRadius: 8 }}>
          <span dangerouslySetInnerHTML={{ __html: i18n.t("statusScheduler.aiDownloadInfo") }} />
        </Alert>
      </Box>

      <Box mb={1} px={2}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
          <InputLabel>{i18n.t("statusScheduler.selectConnection")}</InputLabel>
          <Select
            value={selectedWhatsapp}
            onChange={e => setSelectedWhatsapp(e.target.value)}
            label={i18n.t("statusScheduler.selectConnection")}
            id="select-whatsapp-connection"
          >
            <MenuItem value="all">{i18n.t("statusScheduler.allConnections") || "Todas las conexiones"}</MenuItem>
            {connections.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} px={2} style={{ borderBottom: "1px solid rgba(128,128,128,0.2)", flexWrap: "wrap", gap: 16, paddingBottom: 12 }}>
        <StandardTabs
          value={tabValue}
          onChange={handleTabChange}
          items={[
            {
              value: 0,
              label: i18n.t("statusScheduler.tabs.manual") || "Publicaciones Manuales",
              icon: <EditIcon style={{ color: "#00E676" }} />
            },
            {
              value: 1,
              label: i18n.t("statusScheduler.tabs.aiImage") || "Publicaciones con IA (Imagen)",
              icon: <ImageIcon style={{ color: "#00E5FF" }} />
            },
            {
              value: 2,
              label: "Publicaciones con IA (Video)",
              badge: "BETA",
              icon: <VideocamIcon style={{ color: "#E040FB" }} />
            }
          ]}
        />

        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, alignItems: 'center' }}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".json" onChange={handleImportWeek} />
          <input type="file" ref={dayFileInputRef} style={{ display: 'none' }} accept=".json" onChange={handleImportDay} />
          
          <Tooltip title={i18n.t("statusScheduler.exportWeekTooltip")}>
            <span>
              <IconButton 
                size="small" 
                onClick={handleExportWeek} 
                disabled={schedules.length === 0} 
                style={{ color: schedules.length === 0 ? "#555" : theme.palette.primary.main, padding: "8px" }}
              >
                <GetAppIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={i18n.t("statusScheduler.importWeekTooltip")}>
            <span>
              <IconButton 
                size="small" 
                onClick={() => {
                  if (!selectedWhatsapp || selectedWhatsapp === "all") {
                    showSnack(i18n.t("statusScheduler.selectConnectionTitleWp"), "warning");
                    return;
                  }
                  fileInputRef.current.click();
                }}
                disabled={!selectedWhatsapp || selectedWhatsapp === "all"}
                style={{ color: (!selectedWhatsapp || selectedWhatsapp === "all") ? "#555" : theme.palette.primary.main, padding: "8px" }}
              >
                <PublishIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            className={classes.applyButton}
            startIcon={<AddIcon />}
            onClick={() => {
              if (tabValue === 1) {
                setAiFactoryOpen(true);
              } else if (tabValue === 2) {
                setAiVideoOpen(true);
              } else {
                openAdd();
              }
            }}
            disabled={!selectedWhatsapp || selectedWhatsapp === "all"}
            id="btn-add-status-schedule"
          >
            {tabValue === 1 || tabValue === 2 ? i18n.t("statusScheduler.newAiPost") : (i18n.t("statusScheduler.addButton") || "Agregar Programación")}
          </Button>
        </Box>
      </Box>

      <div className={classes.scrollArea}>
      {!selectedWhatsapp || selectedWhatsapp === "all" ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={6} style={{ gap: 12, color: "#888" }}>
          <Typography variant="h6" style={{ color: "#555" }}>{i18n.t("statusScheduler.selectConnectionTitleWp")}</Typography>
          <Typography variant="body2">{i18n.t("statusScheduler.selectConnectionDescStatus")}</Typography>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={1} style={{ width: "100%", margin: 0 }}>
          {DAYS.map((day, idx) => (
            <Grid item xs key={idx} style={{ minWidth: 150 }}>
              <Box className={classes.dayColumn}>
                <Box
                  className={classes.dayHeader}
                  style={{ backgroundColor: DAY_COLORS[idx], display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span style={{ flex: 1, textAlign: "center" }}>
                    {i18n.t(`statusScheduler.days.${idx}`, { defaultValue: day })}
                  </span>
                  <Box display="flex" alignItems="center">
                    <Tooltip title={i18n.t("statusScheduler.exportDayTooltip", { defaultValue: "Export Day" })}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleExportDay(idx)}
                          disabled={schedulesByDay(idx).length === 0}
                          style={{ color: schedulesByDay(idx).length === 0 ? "rgba(255,255,255,0.3)" : "#fff", padding: 2, marginRight: 2 }}
                        >
                          <GetAppIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={i18n.t("statusScheduler.importDayTooltip", { defaultValue: "Import Day" })}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTargetImportDay(idx);
                            setTimeout(() => dayFileInputRef.current?.click(), 50);
                          }}
                          style={{ color: "#fff", padding: 2, marginRight: 4 }}
                        >
                          <PublishIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={i18n.t("statusScheduler.cloneDayTooltip")}>
                      <IconButton
                        size="small"
                        onClick={() => openCloneDialog(idx)}
                        disabled={schedulesByDay(idx).length === 0}
                        id={`btn-clone-day-${idx}`}
                        style={{ color: schedulesByDay(idx).length === 0 ? "rgba(255,255,255,0.3)" : "#fff", padding: 2 }}
                      >
                        <FileCopyIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("statusScheduler.deleteDayTooltip")}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDay(idx)}
                        disabled={schedulesByDay(idx).length === 0}
                        id={`btn-delete-day-${idx}`}
                        style={{ color: schedulesByDay(idx).length === 0 ? "rgba(255,255,255,0.3)" : "#fff", padding: 2, marginLeft: 2 }}
                      >
                        <DeleteSweepIcon style={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {schedulesByDay(idx).length === 0 && (
                  <Typography style={{ fontSize: "0.78rem", color: "#aaa", textAlign: "center", marginTop: 8 }}>
                    {i18n.t("statusScheduler.noSchedules")}
                  </Typography>
                )}
                {schedulesByDay(idx).map(s => (
                  <Box
                    key={s.id}
                    className={`${classes.scheduleCard} ${!s.isActive ? classes.inactiveCard : ""}`}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <MediaIcon type={s.mediaType} />
                        <Typography style={{ fontSize: "0.82rem", fontWeight: 600, marginLeft: 4, color: "#333" }}>
                          {s.scheduledTime}
                        </Typography>
                        {s.repeatDaily && <RepeatIcon fontSize="small" style={{ color: "#1976d2", marginLeft: 4 }} />}
                      </Box>
                      <Box>
                        <Tooltip title={i18n.t("statusScheduler.cloneStatusTooltip")}>
                          <IconButton size="small" onClick={() => openCloneSingle(s)} id={`btn-clone-single-${s.id}`} style={{ color: "#1976d2" }}>
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {s.status === 'SENT' ? (
                          <Tooltip title="Ver contenido">
                            <IconButton size="small" onClick={() => setViewSchedule(s)} id={`btn-view-${s.id}`} style={{ color: "#1976d2" }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={i18n.t("statusScheduler.edit") || "Editar"}>
                            <IconButton size="small" onClick={() => {
                              if (s.aiGeneration) {
                                setAiEditSchedule(s);
                                setAiFactoryOpen(true);
                              } else {
                                openEdit(s);
                              }
                            }} id={`btn-edit-${s.id}`} style={{ color: "#333" }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={i18n.t("statusScheduler.delete")}>
                          <IconButton size="small" onClick={() => handleDelete(s.id)} id={`btn-delete-${s.id}`}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    {s.caption && (
                      <Typography style={{ fontSize: "0.75rem", color: "#555", marginTop: 12, overflow: "hidden", whiteSpace: "normal", wordBreak: "break-word" }}>
                        {s.mediaType === "text" && s.backgroundColor && (
                          <span className={classes.colorSwatch} style={{ backgroundColor: s.backgroundColor }} />
                        )}
                        <AiPromptViewer prompt={s.caption} companyId={s.companyId} compact={true} />
                        </Typography>
                    )}
                    {s.mediaUrl && (
                      <Box 
                        style={{ marginTop: 8, marginBottom: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, position: 'relative', cursor: 'pointer' }}
                        onClick={() => setPreviewMedia({ url: s.mediaUrl, type: s.mediaType, companyId: s.companyId })}
                      >
                        {(s.mediaType === "image" || (!s.mediaType && s.mediaUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(s.mediaUrl))) && (
                          <img
                            src={getMediaUrl(s.mediaUrl, s.companyId)}
                            alt="Status Preview"
                            style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        {(s.mediaType === "video" || (!s.mediaType && s.mediaUrl && /\.(mp4|webm|ogg)$/i.test(s.mediaUrl))) && (
                          <video
                            src={`${getMediaUrl(s.mediaUrl, s.companyId)}#t=0.1`}
                            style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd", backgroundColor: "#000" }}
                            muted
                            preload="metadata"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        <Typography style={{ fontSize: "0.72rem", color: "#888", alignSelf: "flex-start", marginTop: 4 }}>
                          📎 {s.mediaUrl.split("/").pop()}
                        </Typography>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 4 }}>
                          <ZoomInIcon style={{ color: 'white', fontSize: 32 }} />
                        </div>
                      </Box>
                    )}
                    {s.repeatDaily && s.renewalTime && (
                      <Typography style={{ fontSize: "0.7rem", color: "#1976d2", marginTop: 2 }}>
                        🔁 {i18n.t("statusScheduler.renewsAt")} {s.renewalTime}
                      </Typography>
                    )}
                    {(() => {
                      const connName = s.whatsapp?.name || connections.find(c => c.id === s.whatsappId)?.name || (s.whatsappId === "all" ? i18n.t("statusScheduler.allConnections") : "");
                      return connName ? (
                        <Box mt={1} mb={1}>
                          <span style={{ fontSize: "0.75rem", background: "#eee", color: "#333", padding: "2px 6px", borderRadius: 4, fontWeight: "bold" }}>
                            📱 {connName}
                          </span>
                        </Box>
                      ) : null;
                    })()}
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={s.isActive}
                          onChange={() => handleToggleActive(s)}
                          color="primary"
                          id={`toggle-active-${s.id}`}
                        />
                      }
                      label={<Typography style={{ fontSize: "0.72rem", color: "#333" }}>{s.isActive ? i18n.t("statusScheduler.active") : i18n.t("statusScheduler.inactive")}</Typography>}
                      style={{ marginTop: 4 }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      </div>

      <AiContentFactory
        open={aiFactoryOpen}
        onClose={() => { setAiFactoryOpen(false); setAiEditSchedule(null); }}
        selectedWhatsapp={selectedWhatsapp}
        availableGroups={[]}
        onScheduled={() => { loadSchedules(); setAiFactoryOpen(false); }}
        onRefreshGrid={() => loadSchedules()}
        initialQuantity={1}
        editSchedule={aiEditSchedule}
        type="status"
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>
          {editingId ? i18n.t("statusScheduler.editTitle") : i18n.t("statusScheduler.addTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>{i18n.t("statusScheduler.dayOfWeek")}</InputLabel>
                <Select
                  value={form.dayOfWeek}
                  onChange={e => { const val = e.target.value; setForm(f => ({ ...f, dayOfWeek: val })); }}
                  label={i18n.t("statusScheduler.dayOfWeek")}
                  id="select-day-of-week"
                >
                  {DAYS.map((d, i) => <MenuItem key={i} value={i}>{i18n.t(`statusScheduler.days.${i}`, { defaultValue: d })}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="time"
                label={i18n.t("statusScheduler.time")}
                value={form.scheduledTime}
                onChange={e => { const val = e.target.value; setForm(f => ({ ...f, scheduledTime: val })); }}
                InputLabelProps={{ shrink: true }}
                id="input-scheduled-time"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>{i18n.t("statusScheduler.mediaType")}</InputLabel>
                <Select
                  value={form.mediaType}
                  onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaType: val, mediaUrl: "", caption: "" })); }}
                  label={i18n.t("statusScheduler.mediaType")}
                  id="select-media-type"
                >
                  <MenuItem value="text">📝 {i18n.t("statusScheduler.typeText")}</MenuItem>
                  <MenuItem value="image">🖼️ {i18n.t("statusScheduler.typeImage")}</MenuItem>
                  <MenuItem value="video">🎬 {i18n.t("statusScheduler.typeVideo")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.mediaType === "text" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" variant="outlined" multiline rows={3}
                    label={i18n.t("statusScheduler.caption")}
                    value={form.caption}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                    id="input-caption-text"
                  />
                  <Typography style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#ccff00", textShadow: "0px 0px 8px rgba(204,255,0,0.8)", marginTop: 8 }}>
                    {i18n.t("statusScheduler.markdownTip")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Typography style={{ fontSize: "0.85rem" }}>{i18n.t("statusScheduler.backgroundColor")}:</Typography>
                    <input
                      type="color"
                      value={form.backgroundColor}
                      onChange={e => { const val = e.target.value; setForm(f => ({ ...f, backgroundColor: val })); }}
                      id="input-bg-color"
                      style={{ width: 40, height: 32, border: "none", cursor: "pointer", borderRadius: 4 }}
                    />
                    <span style={{ marginLeft: 8, fontSize: "0.9rem", fontWeight: "bold", color: "#00e5ff", textShadow: "0px 0px 5px rgba(0,229,255,0.8)" }}>{form.backgroundColor}</span>
                  </Box>
                </Grid>
              </>
            )}

            {(form.mediaType === "image" || form.mediaType === "video") && (
              <>
                <Grid item xs={12}>
                  <Box className={classes.fileInputRow}>
                    <TextField
                      fullWidth size="small" variant="outlined"
                      label={i18n.t("statusScheduler.fileUrl")}
                      placeholder="/files/mi-imagen.png"
                      value={form.mediaUrl}
                      onChange={e => { const val = e.target.value; setForm(f => ({ ...f, mediaUrl: val })); }}
                      id="input-media-url"
                    />
                    <Tooltip title={i18n.t("statusScheduler.openFileManager")}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => setSelectorModalOpen(true)}
                        id="btn-open-file-manager"
                        style={{ whiteSpace: "nowrap", padding: "0 20px" }}
                      >
                        {i18n.t("statusScheduler.files")}
                      </Button>
                    </Tooltip>
                  </Box>
                  <Typography style={{ fontSize: "0.75rem", color: "#888", marginTop: 4 }}>
                    {i18n.t("statusScheduler.fileHint")}
                  </Typography>
                  {form.mediaUrl && (
                    <Box mt={2} display="flex" justifyContent="center">
                      <img 
                        src={form.mediaUrl.startsWith("http") ? form.mediaUrl : `${process.env.REACT_APP_BACKEND_URL}/public/${form.mediaUrl.replace(/^\/?(public\/)?/, "")}`} 
                        alt="Preview" 
                        style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain", border: "1px solid #ddd" }}
                        onError={(e) => {
                          if (e.target.dataset.tried) {
                              e.target.style.display = "none";
                              return;
                          }
                          e.target.dataset.tried = "true";
                          e.target.src = `${process.env.REACT_APP_BACKEND_URL}/public/company1/files/${form.mediaUrl.split("/").pop()}`;
                        }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    label={i18n.t("statusScheduler.captionOptional")}
                    value={form.caption}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, caption: val })); }}
                    id="input-caption-media"
                  />
                  <Typography style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#ccff00", textShadow: "0px 0px 8px rgba(204,255,0,0.8)", marginTop: 8 }}>
                    {i18n.t("statusScheduler.markdownTip")}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.repeatDaily}
                    onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, repeatDaily: checked })); }}
                    color="primary"
                    id="toggle-repeat-daily"
                  />
                }
                label={
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <RepeatIcon fontSize="small" style={{ color: "#1976d2" }} />
                      <Typography style={{ fontWeight: 600 }}>{i18n.t("statusScheduler.repeatDaily")}</Typography>
                    </Box>
                    <Typography variant="caption" style={{ marginLeft: 28, fontSize: "0.75rem", fontWeight: "bold", color: "#00e5ff", textShadow: "0px 0px 5px rgba(0,229,255,0.8)" }}>
                      {i18n.t("statusScheduler.renewalSameDayOnly")}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {form.repeatDaily && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    type="time"
                    label={i18n.t("statusScheduler.renewalTime")}
                    value={form.renewalTime}
                    onChange={e => { const val = e.target.value; setForm(f => ({ ...f, renewalTime: val })); }}
                    InputLabelProps={{ shrink: true }}
                    id="input-renewal-time"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box className={classes.infoBox}>
                    ℹ️ {i18n.t("statusScheduler.renewalInfo")}
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={e => { const checked = e.target.checked; setForm(f => ({ ...f, isActive: checked })); }}
                    color="primary"
                    id="toggle-is-active"
                  />
                }
                label={i18n.t("statusScheduler.isActive")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} id="btn-cancel-modal">
            {i18n.t("statusScheduler.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            id="btn-save-schedule"
          >
            {i18n.t("statusScheduler.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <AiVideoModal open={aiVideoOpen} onClose={() => setAiVideoOpen(false)} onVideoGenerated={handleVideoGenerated} />

      <ConfirmationModal
        title={i18n.t("statusScheduler.delete")}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingId(null);
          setDeletingDay(null);
        }}
        onConfirm={executeDelete}
      >
        {deletingDay !== null 
          ? i18n.t("statusScheduler.confirmDeleteDay", { day: i18n.t(`statusScheduler.days.${deletingDay}`, { defaultValue: DAYS[deletingDay] }) })
          : i18n.t("statusScheduler.confirmDelete")
        }
      </ConfirmationModal>

      <FileManagerSelectorModal
        open={selectorModalOpen}
        onClose={() => setSelectorModalOpen(false)}
        onSelect={(path) => setForm(f => ({ ...f, mediaUrl: path }))}
        filterType={form.mediaType}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>

      <FileManagerSelectorModal
        open={selectorModalOpen}
        onClose={() => setSelectorModalOpen(false)}
        onSelect={(path) => setForm(f => ({ ...f, mediaUrl: path }))}
        filterType={form.mediaType}
      />

      {/* [ANTIGRAVITY Plan 880] Diálogo de confirmación de clonación */}
      <Dialog open={cloneDialogOpen} onClose={() => !cloning && setCloneDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>📋 Clonar día de estados</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography style={{ fontSize: "0.9rem", marginBottom: 8 }}>
                Copiar todos los estados del <strong>{cloneFromDay !== null ? DAYS[cloneFromDay] : ""}</strong> hacia:
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Día destino</InputLabel>
                <Select
                  value={cloneToDay !== null ? cloneToDay : ""}
                  onChange={e => setCloneToDay(e.target.value)}
                  label="Día destino"
                  id="select-clone-to-day"
                >
                  {DAYS.map((d, i) => ({ d, i })).filter(item => item.i !== cloneFromDay).map(({ d, i }) => (
                    <MenuItem key={i} value={i}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {cloneToDay !== null && schedulesByDay(cloneToDay).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" style={{ borderRadius: 8 }}>
                  ⚠️ El <strong>{DAYS[cloneToDay]}</strong> ya tiene <strong>{schedulesByDay(cloneToDay).length} estado(s)</strong> programado(s).
                  Al confirmar serán <strong>reemplazados</strong> por los del {cloneFromDay !== null ? DAYS[cloneFromDay] : ""}.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)} disabled={cloning} id="btn-cancel-clone">
            Cancelar
          </Button>
          <Button
            onClick={handleClone}
            color="primary"
            variant="contained"
            disabled={cloning || cloneToDay === null}
            id="btn-confirm-clone"
            startIcon={cloning ? <CircularProgress size={16} /> : <FileCopyIcon />}
          >
            {cloning ? "Clonando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal para Vista Previa (Zoom) */}
      <Dialog open={Boolean(previewMedia)} onClose={() => setPreviewMedia(null)} maxWidth="md" fullWidth>
        <DialogTitle>Vista Previa</DialogTitle>
        <DialogContent style={{ display: 'flex', justifyContent: 'center', background: '#000', padding: 20 }}>
          {previewMedia?.type === 'video' ? (
            <video 
              src={`${getMediaUrl(previewMedia.url, previewMedia.companyId)}#t=0.1`}
              controls 
              style={{ maxWidth: '100%', maxHeight: '70vh' }} 
            />
          ) : (
            <img 
              src={getMediaUrl(previewMedia?.url, previewMedia?.companyId)}
              alt="Zoom Preview" 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
              onError={(e) => {
                  const currentSrc = e.target.src;
                  if (!currentSrc.includes("/messages/") && !currentSrc.includes("/files/")) {
                      e.target.src = `${process.env.REACT_APP_BACKEND_URL}/public/company${previewMedia?.companyId || 1}/messages/${previewMedia?.url?.split("/").pop()}`;
                  } else if (currentSrc.includes("/messages/")) {
                      e.target.src = `${process.env.REACT_APP_BACKEND_URL}/public/company${previewMedia?.companyId || 1}/files/${previewMedia?.url?.split("/").pop()}`;
                  } else {
                      e.target.style.display = "none";
                  }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMedia(null)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Ver Contenido (solo lectura para tarjetas ENVIADAS) */}
      <Dialog open={Boolean(viewSchedule)} onClose={() => setViewSchedule(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon style={{ color: '#1976d2', marginRight: 8 }} />
            Ver contenido enviado
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {viewSchedule && (
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {/* Imagen/Video */}
              {viewSchedule.mediaUrl && (viewSchedule.mediaType === 'image' || (!viewSchedule.mediaType && /\.(jpg|jpeg|png|gif|webp)$/i.test(viewSchedule.mediaUrl))) && (
                <Box textAlign="center">
                  <img
                      src={getMediaUrl(viewSchedule.mediaUrl, viewSchedule.companyId)}
                    alt="Contenido"
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              )}
              {/* Texto / Caption */}
              {viewSchedule.caption && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalTextCaption")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(128, 128, 128, 0.1)',
                      borderRadius: 6,
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.92rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      position: 'relative'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.caption} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              {viewSchedule.aiGeneration && viewSchedule.aiPrompt && (
                <Box>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#00e5ff', textTransform: 'uppercase', letterSpacing: 1, textShadow: '0 0 5px rgba(0,229,255,0.4)' }}>{i18n.t("statusScheduler.modalAiPrompt")}</Typography>
                  <Box
                    style={{
                      marginTop: 6,
                      background: 'rgba(156, 39, 176, 0.1)',
                      borderLeft: '4px solid #9c27b0',
                      borderRadius: '0 6px 6px 0',
                      padding: '10px 14px',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    <AiPromptViewer prompt={viewSchedule.aiPrompt} companyId={viewSchedule.companyId} />
                  </Box>
                </Box>
              )}
              {/* Info adicional */}
              <Box display="flex" style={{ gap: 24, flexWrap: 'wrap' }}>
                {viewSchedule.scheduledTime && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#888' }}>Hora programada</Typography>
                    <Typography>{viewSchedule.scheduledTime}</Typography>
                  </Box>
                )}
                {viewSchedule.dayOfWeek !== undefined && viewSchedule.dayOfWeek !== null && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#888' }}>Día</Typography>
                    <Typography>{["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"][viewSchedule.dayOfWeek] || viewSchedule.dayOfWeek}</Typography>
                  </Box>
                )}
                {viewSchedule.mediaUrl && (
                  <Box>
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#888' }}>{i18n.t("statusScheduler.modalFile")}</Typography>
                    <Typography style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>📎 {viewSchedule.mediaUrl.split("/").pop()}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewSchedule?.caption && (
            <Button 
              onClick={() => { navigator.clipboard.writeText(viewSchedule.caption); }} 
              variant="contained"
              style={{ 
                marginRight: 'auto', 
                background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)', 
                color: 'white', 
                fontWeight: 'bold', 
                boxShadow: '0 3px 10px rgba(0, 229, 255, .4)' 
              }}
            >
              Copiar texto
            </Button>
          )}
          <Button 
            onClick={() => setViewSchedule(null)} 
            variant="contained"
            style={{ 
              background: 'linear-gradient(45deg, #ff1744 30%, #f50057 90%)', 
              color: 'white', 
              fontWeight: 'bold', 
              boxShadow: '0 3px 10px rgba(255, 23, 68, .4)' 
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      </Paper>
    </MainContainer>
  );
}
